import { defineStore } from 'pinia'
import { ref } from 'vue'

/**
 * UI store — owns the MK3-shell flag and shell-level state (sidebar
 * collapse, screen-size). Kept separate from feature stores so the
 * MK3 shell components don't need to reach into business logic.
 *
 * MK3 is the production default. The flag exists as an opt-OUT escape
 * hatch: visiting any URL with `?ui=mk2` flips back to the legacy MK2
 * shell and persists in localStorage; `?ui=mk3` flips back. URL wins
 * over storage on each load.
 *
 * Storage encoding:
 *   absent  → MK3 (default)
 *   '1'     → MK3 (legacy preview opt-in; preserved so users who
 *              opted in pre-cutover stay on MK3 without surprise)
 *   '0'     → MK2 (explicit opt-out via ?ui=mk2)
 */
export const useUiStore = defineStore('ui', () => {
  const initialMk3 = (() => {
    try {
      const url = new URL(window.location.href)
      const param = url.searchParams.get('ui')
      if (param === 'mk3') {
        localStorage.setItem('mk3-shell', '1')
        return true
      }
      if (param === 'mk2') {
        // Persist the opt-out as '0' (not via removeItem) so the
        // default-on logic below sees an explicit non-MK3 marker.
        // removeItem would leave storage absent, which now means
        // "default → MK3" and would silently undo the opt-out on
        // the next page load.
        localStorage.setItem('mk3-shell', '0')
        return false
      }
      // MK3 is the default. Only an explicit '0' (set by ?ui=mk2
      // above) opts out; anything else — absent storage, the
      // legacy '1' marker, or any other value — falls through to
      // MK3.
      return localStorage.getItem('mk3-shell') !== '0'
    } catch {
      // Storage unavailable (private browsing, blocked, etc.) —
      // default to MK3 to match the production rollout.
      return true
    }
  })()

  const useMk3Shell = ref(initialMk3)

  // Sidebar collapse state. Mirrors MK3 reference's navigationStore.isSidebarCollapsed.
  const isSidebarCollapsed = ref(localStorage.getItem('mk3-sidebar-collapsed') === '1')

  // Data labels on dashboard charts. Off by default; agents flip it on to
  // print value labels onto charts for the reports they give customers
  // (TAMK3-28). Persisted per browser so it survives navigation between
  // dashboards within a session.
  const showDataLabels = ref(localStorage.getItem('mk3-data-labels') === '1')

  // Desktop breakpoint matches Tailwind's `lg:` (>= 1024px) — same as MK3 ref.
  const isDesktop = ref(typeof window !== 'undefined' ? window.innerWidth >= 1024 : true)

  function toggleSidebar () {
    isSidebarCollapsed.value = !isSidebarCollapsed.value
    if (isSidebarCollapsed.value) {
      localStorage.setItem('mk3-sidebar-collapsed', '1')
    } else {
      localStorage.removeItem('mk3-sidebar-collapsed')
    }
  }

  function toggleDataLabels () {
    showDataLabels.value = !showDataLabels.value
    if (showDataLabels.value) {
      localStorage.setItem('mk3-data-labels', '1')
    } else {
      localStorage.removeItem('mk3-data-labels')
    }
  }

  function checkScreenSize () {
    if (typeof window !== 'undefined') {
      isDesktop.value = window.innerWidth >= 1024
    }
  }

  return {
    useMk3Shell,
    isSidebarCollapsed,
    isDesktop,
    showDataLabels,
    toggleSidebar,
    toggleDataLabels,
    checkScreenSize,
  }
})
